import { useEffect, useRef } from 'react';
import KeyboardBarcodeScanner from '@point-of-sale/keyboard-barcode-scanner';

type ScanListener = (code: string) => void;

// Pilha de consumidores: apenas o topo (último montado) recebe leituras,
// então um modal armado "rouba" o scanner da página e devolve ao fechar.
const stack: ScanListener[] = [];
let scanner: KeyboardBarcodeScanner | null = null;

const claim = (listener: ScanListener) => {
  stack.push(listener);
  if (!scanner) {
    const instance = new KeyboardBarcodeScanner();
    instance.addEventListener('barcode', (e) => {
      if (scanner === instance && e.value) {
        stack[stack.length - 1]?.(e.value);
      }
    });
    scanner = instance;
    void instance.connect();
  }
};

const release = (listener: ScanListener) => {
  const index = stack.lastIndexOf(listener);
  if (index !== -1) {
    stack.splice(index, 1);
  }
  if (stack.length === 0 && scanner) {
    void scanner.disconnect();
    scanner = null;
  }
};

/**
 * Hook para receber leituras de scanner USB (modo emulação de teclado).
 *
 * O scanner emite bursts rápidos de teclas terminando em Enter. Quando o foco
 * está dentro de um `<input>` ou `<textarea>`, a biblioteca fica inativa e o
 * scanner "digita" diretamente no campo. Fora dessa condição, a leitura vai
 * para o consumidor armado mais recente (topo da pilha).
 *
 * @param onScan - Recebe o valor lido. Pode mudar de identidade livremente.
 * @param enabled - Quando false, este consumidor não entra na pilha.
 */
export const useBarcodeScanner = (onScan: ScanListener, enabled = true) => {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return;
    const listener: ScanListener = (code) => onScanRef.current(code);
    claim(listener);
    return () => release(listener);
  }, [enabled]);
};
